/**
 * Menu real por fecha, y plantilla semanal (Req. 11 y 12).
 *
 * DOS COSAS SEPARADAS A PROPOSITO:
 *
 *   La PLANTILLA dice "los lunes normalmente vendo lasaña".
 *   El MENU dice "el lunes 17 de agosto vendo 5 porciones de lasaña a $120".
 *
 * Editar la plantilla no toca los menus ya generados (Req. 11.4). Si fueran lo
 * mismo, cambiar la costumbre reescribiria el historial, y con el las ganancias
 * de semanas pasadas.
 *
 * DOS DATOS SE CONGELAN al agregar un platillo al menu:
 *
 *   precioVentaCentavos  porque subir el precio manana no debe cambiar lo que
 *                        ya se vendio a otro precio (Req. 11.9)
 *   diasAnticipacion     porque es editable por platillo: una semana la birria
 *                        se cocina un dia antes y otra semana dos
 *
 * `fechaPrep` es campo calculado y SOLO lo escribe este repositorio. Si la
 * interfaz pudiera escribirlo, las dos fechas podrian quedar inconsistentes y
 * la produccion del dia dejaria de cuadrar.
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import { nuevoId } from '../ids';
import { exigirCantidadPositiva, exigirEntero, textoOpcional, exigirTexto } from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, diaSemana, diasDeSemana, type FechaLocal, type DiaSemana } from '@/domain/dates';
import { exigirFechaLocal } from '@/domain/dates';
import { fechaPreparacion } from '@/domain/planning';
import type { Centavos } from '@/domain/money';
import type { MenuTemplate, MenuTemplateItem, PlannedItem, Recipe } from '../types';

const MAX_ANTICIPACION = 30;

// ---------------------------------------------------------------------------
// Menu real por fecha
// ---------------------------------------------------------------------------

export interface DatosPlatillo {
  fechaVenta: FechaLocal;
  recipeId: string;
  porcionesPlaneadas: number;
  /** Si se omite, se toma el de la receta. Editable por platillo (Req. 12.3). */
  diasAnticipacion?: number;
  /** Si se omite, se congela el precio vigente de la receta. */
  precioVentaCentavos?: Centavos;
  notas?: string | null;
}

/**
 * Agrega un platillo a un dia concreto.
 *
 * Se permite el mismo platillo dos veces en un dia (Req. 11.6): puede haber dos
 * pedidos distintos de lasaña para el mismo lunes.
 */
export async function agregarPlatillo(datos: DatosPlatillo): Promise<PlannedItem> {
  const fechaVenta = exigirFechaLocal(datos.fechaVenta, 'fecha de venta');
  const porciones = exigirCantidadPositiva(datos.porcionesPlaneadas, 'porciones');

  const receta = await db.recipes.get(datos.recipeId);
  if (receta === undefined) {
    throw new ErrorValidacion('No se encontro la receta.');
  }

  const dias = exigirEntero(datos.diasAnticipacion ?? receta.diasAnticipacion, 'dias de anticipacion', {
    minimo: 0,
    maximo: MAX_ANTICIPACION,
  });

  const platillo: PlannedItem = {
    id: nuevoId('plan'),
    fechaVenta,
    fechaPrep: fechaPreparacion(fechaVenta, dias),
    recipeId: datos.recipeId,
    porcionesPlaneadas: porciones,
    diasAnticipacion: dias,
    precioVentaCentavos: datos.precioVentaCentavos ?? receta.precioVentaCentavos,
    notas: textoOpcional(datos.notas, 'notas', 200),
    descartadoEn: null,
    creadoEn: ahoraIso(),
  };

  await db.plannedItems.add(platillo);
  return platillo;
}

export interface CambiosPlatillo {
  porcionesPlaneadas?: number;
  diasAnticipacion?: number;
  precioVentaCentavos?: Centavos;
  fechaVenta?: FechaLocal;
  notas?: string | null;
}

/**
 * Modifica un platillo del menu.
 *
 * Cambiar la fecha de venta o la anticipacion recalcula `fechaPrep`, y si ya
 * hay una produccion pendiente, tambien la mueve. Dejarlas desalineadas haria
 * que la produccion apareciera el dia equivocado.
 */
export async function editarPlatillo(id: string, cambios: CambiosPlatillo): Promise<void> {
  const platillo = await db.plannedItems.get(id);
  if (platillo === undefined) {
    throw new ErrorValidacion('No se encontro el platillo del menu.');
  }

  const parche: Partial<PlannedItem> = {};

  const fechaVenta =
    cambios.fechaVenta === undefined
      ? platillo.fechaVenta
      : exigirFechaLocal(cambios.fechaVenta, 'fecha de venta');

  const dias =
    cambios.diasAnticipacion === undefined
      ? platillo.diasAnticipacion
      : exigirEntero(cambios.diasAnticipacion, 'dias de anticipacion', {
          minimo: 0,
          maximo: MAX_ANTICIPACION,
        });

  if (cambios.fechaVenta !== undefined) parche.fechaVenta = fechaVenta;
  if (cambios.diasAnticipacion !== undefined) parche.diasAnticipacion = dias;

  if (cambios.fechaVenta !== undefined || cambios.diasAnticipacion !== undefined) {
    parche.fechaPrep = fechaPreparacion(fechaVenta, dias);
  }

  if (cambios.porcionesPlaneadas !== undefined) {
    parche.porcionesPlaneadas = exigirCantidadPositiva(cambios.porcionesPlaneadas, 'porciones');
  }
  if (cambios.precioVentaCentavos !== undefined) {
    parche.precioVentaCentavos = cambios.precioVentaCentavos;
  }
  if (cambios.notas !== undefined) {
    parche.notas = textoOpcional(cambios.notas, 'notas', 200);
  }

  await enEscritura([db.plannedItems, db.productions], async () => {
    await db.plannedItems.update(id, parche);

    // Las producciones ya completadas NO se tocan: son historial.
    const pendientes = (
      await db.productions.where('plannedItemId').equals(id).toArray()
    ).filter((p) => p.estado === 'pendiente' || p.estado === 'en_proceso');

    for (const produccion of pendientes) {
      await db.productions.update(produccion.id, {
        fechaPrep: parche.fechaPrep ?? produccion.fechaPrep,
        fechaVenta: parche.fechaVenta ?? produccion.fechaVenta,
        porcionesAProducir: parche.porcionesPlaneadas ?? produccion.porcionesAProducir,
      });
    }
  });
}

/**
 * Marca un platillo como NO cocinado, conservando el registro.
 *
 * Es la salida para el platillo que se planeo y no tuvo pedidos: deja de
 * aparecer en el tablero y en produccion, pero sigue en el menu como constancia
 * de que ese dia no se vendio. Borrarlo tambien borraria ese dato.
 *
 * Bloquea si ya se produjo: un lote cocinado es historial y su costo ya salio
 * del inventario. Para eso esta revertir la produccion.
 */
export async function descartarPlatillo(id: string, motivo?: string): Promise<void> {
  const platillo = await db.plannedItems.get(id);
  if (platillo === undefined) {
    throw new ErrorValidacion('No se encontro el platillo del menu.');
  }

  const completadas = (await db.productions.where('plannedItemId').equals(id).toArray()).filter(
    (p) => p.estado === 'completada',
  );
  if (completadas.length > 0) {
    throw new ErrorValidacion(
      'Este platillo ya se cocino, asi que no se puede marcar como no cocinado. ' +
        'Revierte la produccion primero si fue un error.',
    );
  }

  const nota = textoOpcional(motivo, 'motivo', 200);
  const parche: Partial<PlannedItem> = { descartadoEn: ahoraIso() };
  if (nota !== null) {
    parche.notas = platillo.notas === null ? nota : `${platillo.notas} ${nota}`.slice(0, 200);
  }

  await db.plannedItems.update(id, parche);
}

/** Vuelve a poner en pie un platillo descartado. */
export async function reactivarPlatillo(id: string): Promise<void> {
  const platillo = await db.plannedItems.get(id);
  if (platillo === undefined) {
    throw new ErrorValidacion('No se encontro el platillo del menu.');
  }
  await db.plannedItems.update(id, { descartadoEn: null });
}

/** Quita un platillo del menu. Bloquea si ya se produjo o se vendio. */
export async function quitarPlatillo(id: string): Promise<void> {
  const completadas = (await db.productions.where('plannedItemId').equals(id).toArray()).filter(
    (p) => p.estado === 'completada',
  );
  if (completadas.length > 0) {
    throw new ErrorValidacion(
      'Este platillo ya se produjo, asi que no se puede quitar del menu. ' +
        'Revierte la produccion primero si fue un error.',
    );
  }

  const ventas = await db.sales.where('plannedItemId').equals(id).count();
  if (ventas > 0) {
    throw new ErrorValidacion('Este platillo ya tiene ventas registradas.');
  }

  await enEscritura([db.plannedItems, db.productions], async () => {
    await db.productions.where('plannedItemId').equals(id).delete();
    await db.plannedItems.delete(id);
  });
}

// ---------------------------------------------------------------------------
// Consultas del menu
// ---------------------------------------------------------------------------

export async function platillosDeFecha(fechaVenta: FechaLocal): Promise<PlannedItem[]> {
  return db.plannedItems.where('fechaVenta').equals(fechaVenta).toArray();
}

export async function platillosEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<PlannedItem[]> {
  return db.plannedItems.where('fechaVenta').between(desde, hasta, true, true).toArray();
}

/**
 * Platillos cuya PREPARACION cae en el rango.
 *
 * Se consulta aparte porque preparar y vender son dias distintos: la produccion
 * del domingo corresponde a ventas del lunes.
 */
export async function platillosAPrepararEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<PlannedItem[]> {
  return db.plannedItems.where('fechaPrep').between(desde, hasta, true, true).toArray();
}

/** Todo lo que sigue pendiente o vigente desde una fecha. */
export async function platillosDesde(desde: FechaLocal): Promise<PlannedItem[]> {
  return db.plannedItems.where('fechaVenta').aboveOrEqual(desde).toArray();
}

export async function obtenerPlatillo(id: string): Promise<PlannedItem | undefined> {
  return db.plannedItems.get(id);
}

// ---------------------------------------------------------------------------
// Vista del menu con lo necesario para la pantalla
// ---------------------------------------------------------------------------

export interface RenglonMenu {
  platillo: PlannedItem;
  nombreReceta: string;
  /** Rendimiento de la receta, para poder escalar. */
  rendimientoPorciones: number;
  /** Ingreso esperado: porciones por el precio congelado (Req. 11.7). */
  ventaEsperadaCentavos: Centavos;
  /** Estado de la produccion asociada, si existe. */
  estadoProduccion: 'sin_produccion' | 'pendiente' | 'en_proceso' | 'completada' | 'cancelada';
  /**
   * Porciones que de verdad se cocinaron, o null si todavia no se cocina.
   *
   * Con esto la pantalla puede comparar lo cocinado contra lo pedido, que es la
   * comprobacion que evita quedarse corto de comida.
   */
  porcionesCocinadas: number | null;
}

export async function verMenuEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<RenglonMenu[]> {
  const platillos = await platillosEntre(desde, hasta);
  return describirPlatillos(platillos);
}

/** Convierte platillos crudos en renglones listos para mostrar. */
export async function describirPlatillos(
  platillos: readonly PlannedItem[],
): Promise<RenglonMenu[]> {
  if (platillos.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(platillos.map((p) => p.recipeId))]))
      .filter((r): r is Recipe => r !== undefined)
      .map((r) => [r.id, r]),
  );

  const producciones = await db.productions
    .where('plannedItemId')
    .anyOf(platillos.map((p) => p.id))
    .toArray();

  const porPlatillo = new Map<string, (typeof producciones)[number]>();
  for (const produccion of producciones) {
    // Si hubiera varias, manda la que no esta cancelada.
    const previa = porPlatillo.get(produccion.plannedItemId);
    if (previa === undefined || previa.estado === 'cancelada') {
      porPlatillo.set(produccion.plannedItemId, produccion);
    }
  }

  return platillos
    .map((platillo): RenglonMenu => {
      const receta = recetas.get(platillo.recipeId);
      const produccion = porPlatillo.get(platillo.id);

      return {
        platillo,
        nombreReceta: receta?.nombre ?? '(receta eliminada)',
        rendimientoPorciones: receta?.rendimientoPorciones ?? 1,
        ventaEsperadaCentavos: platillo.porcionesPlaneadas * platillo.precioVentaCentavos,
        estadoProduccion: produccion?.estado ?? 'sin_produccion',
        porcionesCocinadas:
          produccion !== undefined && produccion.estado === 'completada'
            ? produccion.porcionesAProducir
            : null,
      };
    })
    .sort(
      (a, b) =>
        a.platillo.fechaVenta.localeCompare(b.platillo.fechaVenta) ||
        a.nombreReceta.localeCompare(b.nombreReceta, 'es'),
    );
}

// ---------------------------------------------------------------------------
// Plantilla semanal
// ---------------------------------------------------------------------------

export async function asegurarPlantilla(nombre = 'Mi semana'): Promise<MenuTemplate> {
  const existente = (await db.menuTemplates.toArray()).find((t) => t.activo);
  if (existente !== undefined) return existente;

  const plantilla: MenuTemplate = {
    id: nuevoId('tpl'),
    nombre: exigirTexto(nombre, 'nombre', { maximo: 60 }),
    activo: true,
    creadoEn: ahoraIso(),
  };

  await db.menuTemplates.add(plantilla);
  return plantilla;
}

export async function definirDiaDePlantilla(datos: {
  diaSemana: DiaSemana;
  recipeId: string;
  porcionesDefault: number;
}): Promise<MenuTemplateItem> {
  const plantilla = await asegurarPlantilla();
  const porciones = exigirCantidadPositiva(datos.porcionesDefault, 'porciones');

  const receta = await db.recipes.get(datos.recipeId);
  if (receta === undefined) throw new ErrorValidacion('No se encontro la receta.');

  const item: MenuTemplateItem = {
    id: nuevoId('tpli'),
    templateId: plantilla.id,
    diaSemana: datos.diaSemana,
    recipeId: datos.recipeId,
    porcionesDefault: porciones,
  };

  await db.menuTemplateItems.add(item);
  return item;
}

export async function quitarDeLaPlantilla(itemId: string): Promise<void> {
  await db.menuTemplateItems.delete(itemId);
}

export async function verPlantilla(): Promise<MenuTemplateItem[]> {
  const plantilla = (await db.menuTemplates.toArray()).find((t) => t.activo);
  if (plantilla === undefined) return [];

  return (await db.menuTemplateItems.where('templateId').equals(plantilla.id).toArray()).sort(
    (a, b) => a.diaSemana - b.diaSemana,
  );
}

export interface ResultadoGeneracion {
  creados: number;
  omitidos: number;
  advertencias: string[];
}

/**
 * Genera el menu de una semana concreta a partir de la plantilla.
 *
 * NO sobrescribe lo que ya exista: si el lunes ya tiene lasaña capturada a
 * mano, la plantilla no la duplica ni la reemplaza. Generar dos veces la misma
 * semana es inofensivo.
 */
export async function generarSemanaDesdePlantilla(
  fechaDeLaSemana: FechaLocal,
): Promise<ResultadoGeneracion> {
  const plantillaItems = await verPlantilla();
  if (plantillaItems.length === 0) {
    return { creados: 0, omitidos: 0, advertencias: ['La plantilla esta vacia.'] };
  }

  const dias = diasDeSemana(fechaDeLaSemana);
  const existentes = await platillosEntre(dias[0] ?? fechaDeLaSemana, dias[6] ?? fechaDeLaSemana);

  const yaHay = new Set(existentes.map((p) => `${p.fechaVenta}|${p.recipeId}`));

  let creados = 0;
  let omitidos = 0;

  for (const item of plantillaItems) {
    const fecha = dias.find((d) => diaSemana(d) === item.diaSemana);
    if (fecha === undefined) continue;

    if (yaHay.has(`${fecha}|${item.recipeId}`)) {
      omitidos += 1;
      continue;
    }

    await agregarPlatillo({
      fechaVenta: fecha,
      recipeId: item.recipeId,
      porcionesPlaneadas: item.porcionesDefault,
    });
    creados += 1;
  }

  return {
    creados,
    omitidos,
    advertencias:
      omitidos > 0
        ? [`Se omitieron ${omitidos} platillos que ya estaban capturados en esa semana.`]
        : [],
  };
}

/** Fecha de referencia para abrir la pantalla del menu. */
export function semanaDeHoy(): FechaLocal {
  return hoy();
}
